import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, Factory, Lightbulb, Star, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Homepage = () => {
  const keyStrengths = [
    {
      icon: <Star className="h-8 w-8 text-yellow-500" />,
      title: "20+ Years Experience",
      description: "Over two decades of expertise in food manufacturing and culinary innovation"
    },
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "Halal & Non-Halal Expertise",
      description: "Comprehensive capabilities across both halal and non-halal food production"
    },
    {
      icon: <Factory className="h-8 w-8 text-blue-500" />,
      title: "Advanced Automation",
      description: "State-of-the-art machinery and automated systems for efficient production"
    },
    {
      icon: <Lightbulb className="h-8 w-8 text-purple-500" />,
      title: "In-house R&D",
      description: "Dedicated research and development team for continuous innovation"
    }
  ];

  const coreServices = [
    {
      title: "Food Manufacturing",
      description: "Large-scale food production with cook-chill and cook-serve capabilities",
      image: "https://images.unsplash.com/photo-1652211955967-99c892925469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjAwMDM3NzN8MA&ixlib=rb-4.1.0&q=85",
      link: "/businesses"
    },
    {
      title: "Catering Services",
      description: "Comprehensive catering solutions for institutional and commercial clients",
      image: "https://images.unsplash.com/photo-1589109807644-924edf14ee09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85",
      link: "/businesses"
    },
    {
      title: "Therapeutic Diets",
      description: "Specialized therapeutic and IDDSI-compliant diet solutions",
      image: "https://customer-assets.emergentagent.com/job_teng-corporate/artifacts/yhvks0dg_Picture9.jpg",
      link: "/expertise/therapeutic-diets"
    },
    {
      title: "OEM Services",
      description: "Original Equipment Manufacturing services for food products",
      image: "https://images.unsplash.com/photo-1651525670099-f828fb5478a5",
      link: "/oem-services"
    }
  ];

  const subsidiaries = [
    {
      name: "First Cuisine Food Industries",
      type: "Halal Manufacturing",
      description: "Leading halal food manufacturer with over 20 years of experience",
      established: "2003"
    },
    {
      name: "PS Catering",
      type: "Non-Halal Manufacturing",
      description: "Specialized non-halal food services for institutional customers",
      established: "2009"
    },
    {
      name: "FC Food Industries",
      type: "Value-Added Production",
      description: "Bakery and frozen food production with innovative solutions",
      established: "2014"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Modern Design with Image Background */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_tengfood-site/artifacts/2sba35cb_WhatsApp%20Image%202025-12-08%20at%2012.48.37.jpeg" 
            alt="Automated Food Production"
            className="w-full h-full object-cover"
          />
          {/* Lighter Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-700/40 via-blue-600/35 to-slate-600/40"></div>
        </div>

        <div className="relative w-full h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8 border border-white/20">
              <span className="text-gray-100 text-sm font-medium">🏆 Over 20 Years of Excellence</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold leading-tight">
              <div className="text-white">Innovative Food</div>
              <div 
                className="mt-2" 
                style={{ 
                  fontFamily: "'Montserrat', sans-serif",
                  color: "#C5B358",
                  fontWeight: 500,
                  letterSpacing: "0.02em"
                }}
              >
                Manufacturing Solutions
              </div>
            </h1>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 lg:py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-6">
              About Teng Food Holdings
            </h2>
            <p className="text-lg text-brand-charcoal leading-relaxed">
              Established in 2022, Teng Food Holdings serves as the parent company uniting three 
              specialized subsidiaries with decades of combined expertise in food manufacturing, 
              catering, and innovative culinary solutions. We cater to both business and consumer 
              markets with a steadfast commitment to quality, safety, and innovation. Teng Food 
              operates a state-of-the-art central kitchen employing a Cook-Chill production model 
              to ensure consistency, efficiency, and freshness across all operations.
            </p>
          </div>

          {/* Key Strengths Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyStrengths.map((strength, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300 bg-white border-brand-gray">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {strength.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-brand-charcoal mb-3">{strength.title}</h3>
                  <p className="text-brand-charcoal">{strength.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-6">
              Core Services
            </h2>
            <p className="text-lg text-brand-charcoal max-w-3xl mx-auto">
              From large-scale food manufacturing to specialized therapeutic diets, 
              we deliver comprehensive solutions across the food industry spectrum.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coreServices.map((service, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-brand-gray">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-brand-charcoal mb-3">{service.title}</h3>
                  <p className="text-brand-charcoal mb-4">{service.description}</p>
                  <Button variant="outline" className="text-brand-red border-brand-red hover:bg-brand-red hover:text-white">
                    <Link to={service.link} className="flex items-center">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Subsidiaries Section */}
      <section className="py-16 lg:py-24 bg-brand-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-charcoal mb-6">
              Our Subsidiaries
            </h2>
            <p className="text-lg text-brand-charcoal max-w-3xl mx-auto">
              Three specialized companies united under Teng Food Holdings, 
              each bringing unique expertise and capabilities to our group.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {subsidiaries.map((subsidiary, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300 bg-white border-brand-gray">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-brand-red font-medium">{subsidiary.type}</div>
                    <div className="text-sm text-brand-gray">Est. {subsidiary.established}</div>
                  </div>
                  <h3 className="text-xl font-semibold text-brand-charcoal mb-3">{subsidiary.name}</h3>
                  <p className="text-brand-charcoal mb-4">{subsidiary.description}</p>
                  <Button variant="outline" size="sm" className="text-brand-sky border-brand-sky hover:bg-brand-sky hover:text-white">
                    <Link to="/businesses" className="flex items-center">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-r from-brand-blue to-brand-red text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-xl mb-8 text-brand-light max-w-3xl mx-auto">
            Whether you need OEM manufacturing services, catering solutions, or specialized therapeutic diets, 
            we&apos;re here to deliver excellence tailored to your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-brand-red hover:bg-brand-light hover:text-brand-red transition-colors">
              <Link to="/contact" className="flex items-center">
                Contact Us Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-blue transition-colors">
              <Link to="/oem-services">OEM Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;